/**
 * React Query hooks for Campaign operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import campaignService from '../services/campaign.service';

interface UseCampaignDetailQueryParams {
  campaignId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCampaignDetailQuery = ({
  campaignId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCampaignDetailQueryParams) => {
  return useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      if (!campaignId || !workspaceId || !idToken) {
        throw new Error('Campaign ID, Workspace ID and token are required');
      }
      return await campaignService.getCampaign(campaignId, workspaceId, idToken);
    },
    enabled: enabled && !!campaignId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseCampaignListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useCampaignListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 10,
  sortBy = 'created_at',
  order = 'desc',
  search,
  status,
  enabled = true,
  refetchInterval = 5000,
}: UseCampaignListQueryParams) => {
  return useQuery({
    queryKey: ['campaigns', workspaceId, page, size, sortBy, order, search, status],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await campaignService.listCampaigns(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
        status,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};
